import moment from 'moment';

/**
 * Chart Analytics Utility
 * Provides comprehensive analytics for chart data including trends, peaks, changes, and patterns
 */

/**
 * Calculate trend direction and strength
 * @param {Array} chartData - Array of chart data points with x, y, z properties
 * @returns {Object} Trend analysis including direction, strength, and description
 */
export function calculateTrend(chartData) {
  if (!chartData || chartData.length < 2) {
    return {
      direction: 'insufficient_data',
      strength: 0,
      description: 'Not enough data points to determine trend',
      slope: 0,
      change: 0,
      changePercentage: 0
    };
  }

  // Calculate linear regression slope
  const n = chartData.length;
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
  
  chartData.forEach((point, index) => {
    const x = index;
    const y = point.y || 0;
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumX2 += x * x;
  });

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  
  // Calculate first and last values for overall change
  const firstValue = chartData[0].y || 0;
  const lastValue = chartData[chartData.length - 1].y || 0;
  const change = lastValue - firstValue;
  const changePercentage = firstValue !== 0 ? ((change / firstValue) * 100).toFixed(1) : 0;

  // Determine trend direction and strength
  let direction, strength, description;
  
  if (Math.abs(slope) < 0.1) {
    direction = 'stable';
    strength = 'none';
    description = 'Data shows stable pattern with minimal variation';
  } else if (slope > 0) {
    direction = 'upward';
    if (slope > 1) {
      strength = 'strong';
      description = `Strong upward trend with ${changePercentage}% increase`;
    } else if (slope > 0.5) {
      strength = 'moderate';
      description = `Moderate upward trend with ${changePercentage}% increase`;
    } else {
      strength = 'slight';
      description = `Slight upward trend with ${changePercentage}% increase`;
    }
  } else {
    direction = 'downward';
    if (slope < -1) {
      strength = 'strong';
      description = `Strong downward trend with ${Math.abs(changePercentage)}% decrease`;
    } else if (slope < -0.5) {
      strength = 'moderate';
      description = `Moderate downward trend with ${Math.abs(changePercentage)}% decrease`;
    } else {
      strength = 'slight';
      description = `Slight downward trend with ${Math.abs(changePercentage)}% decrease`;
    }
  }

  return {
    direction,
    strength,
    description,
    slope: parseFloat(slope.toFixed(3)),
    change,
    changePercentage: parseFloat(changePercentage),
    firstValue,
    lastValue,
    dataPoints: n
  };
}

/**
 * Find peak and valley in chart data
 * @param {Array} chartData - Array of chart data points
 * @returns {Object} Peak and valley information
 */
export function findPeakAndValley(chartData) {
  if (!chartData || chartData.length === 0) {
    return { peak: null, valley: null };
  }

  let peak = { ...chartData[0], index: 0 };
  let valley = { ...chartData[0], index: 0 };

  chartData.forEach((point, index) => {
    if ((point.y || 0) > (peak.y || 0)) {
      peak = { ...point, index };
    }
    if ((point.y || 0) < (valley.y || 0)) {
      valley = { ...point, index };
    }
  });

  return {
    peak: {
      value: peak.y || 0,
      label: peak.z,
      date: peak.x,
      index: peak.index,
      percentage: peak.percentage || 0,
      description: `Peak of ${peak.y} transfers on ${peak.z}`
    },
    valley: {
      value: valley.y || 0,
      label: valley.z,
      date: valley.x,
      index: valley.index,
      percentage: valley.percentage || 0,
      description: `Valley of ${valley.y} transfers on ${valley.z}`
    }
  };
}

/**
 * Calculate period-over-period changes (week-over-week, day-over-day, etc.)
 * @param {Array} chartData - Array of chart data points
 * @returns {Array} Array of changes with details
 */
export function calculatePeriodOverPeriodChanges(chartData) {
  if (!chartData || chartData.length < 2) {
    return [];
  }

  const changes = [];
  
  for (let i = 1; i < chartData.length; i++) {
    const current = chartData[i];
    const previous = chartData[i - 1];
    
    const currentValue = current.y || 0;
    const previousValue = previous.y || 0;
    const change = currentValue - previousValue;
    const changePercentage = previousValue !== 0 
      ? parseFloat(((change / previousValue) * 100).toFixed(1)) 
      : (currentValue > 0 ? 100 : 0);

    changes.push({
      fromPeriod: previous.z,
      toPeriod: current.z,
      fromValue: previousValue,
      toValue: currentValue,
      change,
      changePercentage,
      direction: change > 0 ? 'increase' : change < 0 ? 'decrease' : 'stable',
      description: `${previous.z} → ${current.z}: ${change >= 0 ? '+' : ''}${change} (${change >= 0 ? '+' : ''}${changePercentage}%)`,
      index: i
    });
  }

  return changes;
}

/**
 * Calculate statistical summary of chart data
 * @param {Array} chartData - Array of chart data points
 * @returns {Object} Statistical summary
 */
export function calculateStatistics(chartData) {
  if (!chartData || chartData.length === 0) {
    return {
      count: 0,
      sum: 0,
      mean: 0,
      median: 0,
      min: 0,
      max: 0,
      range: 0,
      stdDev: 0
    };
  }

  const values = chartData.map(point => point.y || 0);
  const count = values.length;
  const sum = values.reduce((acc, val) => acc + val, 0);
  const mean = parseFloat((sum / count).toFixed(2));
  
  // Calculate median
  const sorted = [...values].sort((a, b) => a - b);
  const median = count % 2 === 0
    ? (sorted[count / 2 - 1] + sorted[count / 2]) / 2
    : sorted[Math.floor(count / 2)];
  
  // Calculate standard deviation
  const squaredDiffs = values.map(val => Math.pow(val - mean, 2));
  const variance = squaredDiffs.reduce((acc, val) => acc + val, 0) / count;
  const stdDev = parseFloat(Math.sqrt(variance).toFixed(2));

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;

  return {
    count,
    sum,
    mean,
    median,
    min,
    max,
    range,
    stdDev,
    description: `Average: ${mean}, Median: ${median}, Range: ${min}-${max}`
  };
}

/**
 * Detect patterns in chart data (spikes, drops, cycles)
 * @param {Array} chartData - Array of chart data points
 * @returns {Object} Pattern detection results
 */
export function detectPatterns(chartData) {
  if (!chartData || chartData.length < 3) {
    return {
      spikes: [],
      drops: [],
      patterns: [],
      description: 'Insufficient data for pattern detection'
    };
  }

  const stats = calculateStatistics(chartData);
  const threshold = stats.mean + stats.stdDev;
  const dropThreshold = stats.mean - stats.stdDev;

  const spikes = [];
  const drops = [];

  chartData.forEach((point, index) => {
    const value = point.y || 0;
    
    if (value > threshold && index > 0) {
      const previousValue = chartData[index - 1].y || 0;
      const increasePercentage = previousValue !== 0 
        ? parseFloat(((value - previousValue) / previousValue * 100).toFixed(1))
        : 100;
      
      spikes.push({
        period: point.z,
        value,
        date: point.x,
        index,
        increaseFromPrevious: value - previousValue,
        increasePercentage,
        description: `Spike at ${point.z}: ${value} transfers (${increasePercentage >= 0 ? '+' : ''}${increasePercentage}% from previous period)`
      });
    }

    if (value < dropThreshold && index > 0) {
      const previousValue = chartData[index - 1].y || 0;
      const decreasePercentage = previousValue !== 0 
        ? parseFloat(((value - previousValue) / previousValue * 100).toFixed(1))
        : 0;
      
      drops.push({
        period: point.z,
        value,
        date: point.x,
        index,
        decreaseFromPrevious: value - previousValue,
        decreasePercentage,
        description: `Drop at ${point.z}: ${value} transfers (${decreasePercentage}% from previous period)`
      });
    }
  });

  // Detect recurring patterns (weekly, monthly cycles)
  const patterns = [];
  if (chartData.length >= 7) {
    // Check for weekly patterns
    const weeklyAverages = {};
    chartData.forEach(point => {
      const dayOfWeek = moment(point.x).format('dddd');
      if (!weeklyAverages[dayOfWeek]) {
        weeklyAverages[dayOfWeek] = { sum: 0, count: 0 };
      }
      weeklyAverages[dayOfWeek].sum += point.y || 0;
      weeklyAverages[dayOfWeek].count += 1;
    });

    const weeklyPattern = Object.entries(weeklyAverages).map(([day, data]) => ({
      day,
      average: parseFloat((data.sum / data.count).toFixed(2))
    })).sort((a, b) => b.average - a.average);

    if (weeklyPattern.length > 0) {
      patterns.push({
        type: 'weekly',
        description: `Busiest day: ${weeklyPattern[0].day} (avg ${weeklyPattern[0].average} transfers)`,
        details: weeklyPattern
      });
    }
  }

  return {
    spikes,
    drops,
    patterns,
    hasSpikes: spikes.length > 0,
    hasDrops: drops.length > 0,
    hasPatterns: patterns.length > 0,
    description: `Found ${spikes.length} spike(s), ${drops.length} drop(s), and ${patterns.length} pattern(s)`
  };
}

/**
 * Analyze what happened after a peak
 * @param {Array} chartData - Array of chart data points
 * @param {Object} peak - Peak information
 * @returns {Object} Post-peak analysis
 */
export function analyzePostPeak(chartData, peak) {
  if (!chartData || !peak || peak.index >= chartData.length - 1) {
    return {
      available: false,
      description: 'No data available after peak'
    };
  }

  const peakIndex = peak.index;
  const peakValue = peak.value;
  const postPeakData = chartData.slice(peakIndex + 1);
  
  if (postPeakData.length === 0) {
    return {
      available: false,
      description: 'Peak occurs at the end of the period'
    };
  }

  const immediateNextValue = postPeakData[0].y || 0;
  const immediateChange = immediateNextValue - peakValue;
  const immediateChangePercentage = peakValue !== 0 
    ? parseFloat(((immediateChange / peakValue) * 100).toFixed(1))
    : 0;

  const postPeakStats = calculateStatistics(postPeakData);
  const postPeakTrend = calculateTrend(postPeakData);

  return {
    available: true,
    immediateResponse: {
      nextPeriod: postPeakData[0].z,
      nextValue: immediateNextValue,
      change: immediateChange,
      changePercentage: immediateChangePercentage,
      description: `After the peak of ${peakValue}, transfers ${immediateChange < 0 ? 'dropped' : 'increased'} to ${immediateNextValue} (${immediateChangePercentage >= 0 ? '+' : ''}${immediateChangePercentage}%)`
    },
    overallPostPeakTrend: {
      ...postPeakTrend,
      averageAfterPeak: postPeakStats.mean,
      stabilized: postPeakStats.stdDev < postPeakStats.mean * 0.3,
      description: `Post-peak average stabilized at ${postPeakStats.mean.toFixed(1)} transfers per period`
    },
    analysis: immediateChange < -peakValue * 0.3 
      ? 'Significant drop after peak suggests event-driven spike'
      : immediateChange < 0 
        ? 'Moderate decrease after peak, returning to baseline'
        : 'Continued high activity after peak',
    periodsAfterPeak: postPeakData.length
  };
}

/**
 * Compare two time periods
 * @param {Array} period1Data - First period chart data
 * @param {Array} period2Data - Second period chart data
 * @param {String} period1Label - Label for first period
 * @param {String} period2Label - Label for second period
 * @returns {Object} Comparison analysis
 */
export function comparePeriods(period1Data, period2Data, period1Label, period2Label) {
  if (!period1Data || !period2Data || period1Data.length === 0 || period2Data.length === 0) {
    return {
      available: false,
      description: 'Insufficient data for comparison'
    };
  }

  const stats1 = calculateStatistics(period1Data);
  const stats2 = calculateStatistics(period2Data);

  const totalChange = stats2.sum - stats1.sum;
  const totalChangePercentage = stats1.sum !== 0 
    ? parseFloat(((totalChange / stats1.sum) * 100).toFixed(1))
    : 0;

  const avgChange = stats2.mean - stats1.mean;
  const avgChangePercentage = stats1.mean !== 0 
    ? parseFloat(((avgChange / stats1.mean) * 100).toFixed(1))
    : 0;

  return {
    available: true,
    period1: {
      label: period1Label,
      total: stats1.sum,
      average: stats1.mean,
      median: stats1.median,
      range: `${stats1.min}-${stats1.max}`
    },
    period2: {
      label: period2Label,
      total: stats2.sum,
      average: stats2.mean,
      median: stats2.median,
      range: `${stats2.min}-${stats2.max}`
    },
    comparison: {
      totalChange,
      totalChangePercentage,
      avgChange,
      avgChangePercentage,
      direction: totalChange > 0 ? 'increased' : totalChange < 0 ? 'decreased' : 'stable',
      description: `${period2Label} ${totalChange >= 0 ? 'increased' : 'decreased'} by ${Math.abs(totalChange)} transfers (${totalChangePercentage >= 0 ? '+' : ''}${totalChangePercentage}%) compared to ${period1Label}`
    }
  };
}

/**
 * Generate comprehensive chart analysis
 * @param {Array} chartData - Array of chart data points
 * @param {String} aggregationType - Type of aggregation (daily, weekly, monthly)
 * @returns {Object} Comprehensive analysis
 */
export function generateChartAnalysis(chartData, aggregationType = 'unknown') {
  if (!chartData || chartData.length === 0) {
    return {
      available: false,
      description: 'No chart data available for analysis'
    };
  }

  const trend = calculateTrend(chartData);
  const peakValley = findPeakAndValley(chartData);
  const periodChanges = calculatePeriodOverPeriodChanges(chartData);
  const statistics = calculateStatistics(chartData);
  const patterns = detectPatterns(chartData);
  const postPeakAnalysis = peakValley.peak ? analyzePostPeak(chartData, peakValley.peak) : null;

  // Find largest increase and decrease
  const largestIncrease = periodChanges.length > 0 
    ? periodChanges.reduce((max, change) => change.change > max.change ? change : max, periodChanges[0])
    : null;
  
  const largestDecrease = periodChanges.length > 0 
    ? periodChanges.reduce((min, change) => change.change < min.change ? change : min, periodChanges[0])
    : null;

  return {
    available: true,
    aggregationType,
    dataPoints: chartData.length,
    dateRange: {
      start: chartData[0].z,
      end: chartData[chartData.length - 1].z,
      startDate: chartData[0].x,
      endDate: chartData[chartData.length - 1].x
    },
    trend,
    peak: peakValley.peak,
    valley: peakValley.valley,
    statistics,
    periodOverPeriodChanges: periodChanges,
    largestIncrease,
    largestDecrease,
    patterns,
    postPeakAnalysis,
    summary: {
      overallTrend: trend.description,
      peakDescription: peakValley.peak?.description,
      averageValue: statistics.mean,
      volatility: statistics.stdDev < statistics.mean * 0.3 ? 'Low' : statistics.stdDev < statistics.mean * 0.6 ? 'Moderate' : 'High',
      dataQuality: chartData.length >= 7 ? 'Good' : chartData.length >= 3 ? 'Fair' : 'Limited'
    },
    // Raw chart data for AI reference
    chartData: chartData.map(point => ({
      period: point.z,
      date: point.x,
      value: point.y || 0,
      percentage: point.percentage || 0,
      facilityIds: point.facilityIds || [],
      // Reduce payload: only pass patient id and dateOfAdt per facility
      facilityWiseData: (() => {
        const source = point.facilityWiseData || {};
        if (!source || typeof source !== 'object') return {};
        return Object.fromEntries(
          Object.entries(source).map(([facilityId, patients]) => {
            const reduced = Array.isArray(patients)
              ? patients.map(p => ({
                  id: p._id || p.id,
                  dateOfAdt: p.dateOfADT || p.dateOfAdt || p.dateOfADTOriginal || p.dateOfAdtOriginal
                }))
              : [];
            return [facilityId, reduced];
          })
        );
      })()
    }))
  };
}

const chartAnalytics = {
  calculateTrend,
  findPeakAndValley,
  calculatePeriodOverPeriodChanges,
  calculateStatistics,
  detectPatterns,
  analyzePostPeak,
  comparePeriods,
  generateChartAnalysis
};

export default chartAnalytics;

